import { Stack } from "@mui/material"
import { ReactElement, useContext, useMemo } from "react"

import { Datasets } from "../../../../../constants/enums"
import {
    EVAC_AC_ONLY_REG,
    EVAC_ADDITIONAL_COMMENTS_REG,
    EVAC_BARCODE_REG,
    EVAC_MAX_CURRENT_REG,
    EVAC_MODEL_REF_REG,
    EVAC_MODEL_REG,
    EVAC_PHASES_REG,
    EVAC_V2G_CAPABILITY_REG,
    EVAC_V2G_EXPORT_CAPACITY_REG,
    EVAC_V2G_G99_REG,
    EVAC_VOLTAGE_REG,
    evacDeviceFormLabelMapping,
    v2gG99Options,
} from "../../../../../constants/lct/evac/deviceForm"
import {
    CORE_ADDITIONAL_COMMENTS_REG,
    CORE_CAPACITY_REG,
    CORE_CATEGORY_REG,
    CORE_HARDWARE_VERSION_REG,
    CORE_MANUFACTURER_REG,
    CORE_MODEL_REG,
    CORE_PHASES_REG,
    CORE_SOFTWARE_VERSION_REG,
    CORE_TYPE_REG,
    registerDeviceLabelMapping,
} from "../../../../../constants/ttr/registerDevice"
import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import useUploadedDeviceAttachments from "../../../../../services/middleware/hooks/devices/common/useUploadedDeviceAttachments"
import { booleanToYesNo } from "../../../../../services/middleware/hooks/devices/lct/postBodyGenerators"
import {
    getDisplayName,
    getFullyTestedLabelName,
    getPartiallyTestedLabelName,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import FormSummary from "../../../../formSummary/formSummary"
import Confirmation from "../../../../popUps/registerDevice/confirmation"
import { CreateV2GBundleContext } from "../providers/baseProviders/createV2GBundleProvider"

const mapAttachmentSummaries = (
    attachments: Array<{
        description: string
        fileName?: string
        file?: { name: string }
    }>
): Record<string, string | number | boolean | undefined> =>
    attachments.reduce(
        (
            acc: Record<string, string | number | boolean | undefined>,
            attachment
        ) => ({
            ...acc,
            [attachment.fileName ?? attachment.file?.name ?? "Attachment"]:
                attachment.description || "Uploaded file",
        }),
        {}
    )

const ReviewAndSubmitV2GBundle = (): ReactElement => {
    const {
        currentIndex,
        setCurrentIndex,
        canEditManufacturer,
        evacFormData,
        ttrFormData,
        generationMode,
        selectedInverter,
        evacAttachmentUUID,
        createdEvacDeviceRef,
        submitLoading,
        submitSuccessful,
        onSubmit,
        errors,
    } = useContext(CreateV2GBundleContext)

    const {
        manufacturerMappings,
        deviceCategoryMappings,
        deviceTypeMappings,
        devicePhaseMappings,
    } = useFilterReferenceMappings()

    const { uploadedAttachments: uploadedEvacAttachments } =
        useUploadedDeviceAttachments(
            Datasets.EVAC,
            createdEvacDeviceRef,
            evacAttachmentUUID,
            false
        )

    const typeTestSummary = useMemo(() => {
        const mappings: Record<string, string | number | boolean | undefined> =
            {}

        ttrFormData.typeTestDetails.forEach((detail) => {
            mappings[getFullyTestedLabelName(detail.displayName)] =
                detail.fullyTested ? "Yes" : "No"
            mappings[getPartiallyTestedLabelName(detail.displayName)] =
                detail.partiallyTested ? "Yes" : "No"
        })

        return mappings
    }, [ttrFormData.typeTestDetails])

    const getStepIndex = (
        step: "evac" | "existing" | "genCore" | "typeTest" | "evacFiles"
    ): number => {
        const baseOffset = canEditManufacturer ? 1 : 0

        if (generationMode === "existing") {
            const existingMap = {
                evac: baseOffset,
                existing: baseOffset + 2,
                genCore: baseOffset + 2,
                typeTest: baseOffset + 2,
                evacFiles: baseOffset + 3,
            }

            return existingMap[step]
        }

        const newMap = {
            evac: baseOffset,
            existing: baseOffset + 2,
            genCore: baseOffset + 2,
            typeTest: baseOffset + 3,
            evacFiles: baseOffset + 4,
        }

        return newMap[step]
    }

    if (errors.length > 0) {
        return <ErrorDisplay error={errors[0]} />
    }

    return (
        <>
            <Confirmation
                isOpen={submitSuccessful}
                deviceRef={createdEvacDeviceRef}
                dataset={Datasets.EVAC}
            />
            <Stack spacing={2} paddingTop={2}>
                <FormHeader
                    header="Review and Submit"
                    subHeader="Please review the EVCP and inverter details before submitting the V2G bundle"
                    index={currentIndex + 1}
                    headerTypography={{
                        color: "text.primary",
                    }}
                    subHeaderTypography={{
                        color: "text.secondary",
                    }}
                />
                <FormSummary
                    summaries={[
                        {
                            title: "EV Charge Point (AC)",
                            mappings: {
                                [evacDeviceFormLabelMapping[EVAC_MODEL_REG]]:
                                    evacFormData.deviceModel,
                                [evacDeviceFormLabelMapping[
                                    EVAC_MODEL_REF_REG
                                ]]: evacFormData.deviceModelRef,
                                [evacDeviceFormLabelMapping[EVAC_BARCODE_REG]]:
                                    evacFormData.barcodeNumber,
                                [evacDeviceFormLabelMapping[EVAC_PHASES_REG]]:
                                    evacFormData.phases ?? undefined,
                                [evacDeviceFormLabelMapping[EVAC_VOLTAGE_REG]]:
                                    evacFormData.voltage ?? undefined,
                                [evacDeviceFormLabelMapping[
                                    EVAC_MAX_CURRENT_REG
                                ]]:
                                    evacFormData.maxCurrentAmpPerPhase ??
                                    undefined,
                                [evacDeviceFormLabelMapping[EVAC_AC_ONLY_REG]]:
                                    typeof evacFormData.acOnlyOutput ===
                                    "boolean"
                                        ? booleanToYesNo(
                                              evacFormData.acOnlyOutput
                                          )
                                        : undefined,
                                [evacDeviceFormLabelMapping[
                                    EVAC_V2G_CAPABILITY_REG
                                ]]: "Yes",
                                [evacDeviceFormLabelMapping[
                                    EVAC_V2G_EXPORT_CAPACITY_REG
                                ]]: evacFormData.v2gExportCapacity ?? undefined,
                                [evacDeviceFormLabelMapping[EVAC_V2G_G99_REG]]:
                                    getDisplayName(
                                        v2gG99Options,
                                        evacFormData.v2gG99gbG999niBoth
                                    ) ?? undefined,
                                [evacDeviceFormLabelMapping[
                                    EVAC_ADDITIONAL_COMMENTS_REG
                                ]]: evacFormData.comments,
                            },
                            onEdit: submitLoading
                                ? undefined
                                : () => setCurrentIndex(getStepIndex("evac")),
                        },
                        {
                            title:
                                generationMode === "existing"
                                    ? "Selected Existing Inverter"
                                    : "New Inverter",
                            mappings:
                                generationMode === "existing"
                                    ? {
                                          "System Reference":
                                              selectedInverter?.deviceReference,
                                          Manufacturer:
                                              selectedInverter?.manufacturer,
                                          Model: selectedInverter?.model,
                                          Type: selectedInverter?.type,
                                          "Capacity (kW)":
                                              selectedInverter?.capacity,
                                          Phase: selectedInverter?.phases,
                                      }
                                    : {
                                          [registerDeviceLabelMapping[
                                              CORE_MANUFACTURER_REG
                                          ]]: ttrFormData.coreDetails
                                              .manufacturer
                                              ? (getDisplayName(
                                                    manufacturerMappings,
                                                    Number(
                                                        ttrFormData.coreDetails
                                                            .manufacturer
                                                    )
                                                ) ?? undefined)
                                              : undefined,
                                          [registerDeviceLabelMapping[
                                              CORE_MODEL_REG
                                          ]]: ttrFormData.coreDetails.model,
                                          [registerDeviceLabelMapping[
                                              CORE_CATEGORY_REG
                                          ]]:
                                              getDisplayName(
                                                  deviceCategoryMappings,
                                                  ttrFormData.coreDetails
                                                      .deviceCategory
                                              ) ?? undefined,
                                          [registerDeviceLabelMapping[
                                              CORE_TYPE_REG
                                          ]]:
                                              getDisplayName(
                                                  deviceTypeMappings,
                                                  ttrFormData.coreDetails
                                                      .deviceType
                                              ) ?? undefined,
                                          [registerDeviceLabelMapping[
                                              CORE_CAPACITY_REG
                                          ]]:
                                              ttrFormData.coreDetails
                                                  .registeredCapacity ??
                                              undefined,
                                          [registerDeviceLabelMapping[
                                              CORE_PHASES_REG
                                          ]]:
                                              getDisplayName(
                                                  devicePhaseMappings,
                                                  ttrFormData.coreDetails
                                                      .numberOfPhases
                                              ) ?? undefined,
                                          [registerDeviceLabelMapping[
                                              CORE_SOFTWARE_VERSION_REG
                                          ]]:
                                              ttrFormData.coreDetails
                                                  .softwareVersion,
                                          [registerDeviceLabelMapping[
                                              CORE_HARDWARE_VERSION_REG
                                          ]]:
                                              ttrFormData.coreDetails
                                                  .hardwareVersion,
                                          [registerDeviceLabelMapping[
                                              CORE_ADDITIONAL_COMMENTS_REG
                                          ]]:
                                              ttrFormData.coreDetails
                                                  .additionalComments,
                                      },
                            onEdit: submitLoading
                                ? undefined
                                : () =>
                                      setCurrentIndex(
                                          generationMode === "existing"
                                              ? getStepIndex("existing")
                                              : getStepIndex("genCore")
                                      ),
                        },
                        {
                            title: "EVCP Supporting Files",
                            mappings: mapAttachmentSummaries(
                                (uploadedEvacAttachments ??
                                    evacFormData.stagedAttachments) as Array<{
                                    description: string
                                    fileName?: string
                                    file?: { name: string }
                                }>
                            ),
                            onEdit: submitLoading
                                ? undefined
                                : () =>
                                      setCurrentIndex(
                                          getStepIndex("evacFiles")
                                      ),
                        },
                        {
                            title: "Type Test Details",
                            mappings: typeTestSummary,
                            omit: generationMode !== "new",
                            onEdit: submitLoading
                                ? undefined
                                : () =>
                                      setCurrentIndex(getStepIndex("typeTest")),
                        },
                    ]}
                />
                <FormNavigation
                    onClickBack={() => setCurrentIndex(currentIndex - 1)}
                    onClickNext={onSubmit}
                    nextText="Submit"
                    allDisabled={submitLoading}
                />
            </Stack>
        </>
    )
}

export default ReviewAndSubmitV2GBundle
